<template>
  <div class="sec-lvc-wrap">
    <div ref="root" class="sec-lvc">
      <!-- Rows are animated by this component's own timeline, not the page-wide data-reveal. -->
      <div v-for="lv in levels" :key="lv.n" class="sec-lvc__row">
        <div class="sec-lvc__label">
          <span class="m700 sec-lvc__n">{{ lv.n }}</span>
          <span class="o600 sec-lvc__name">{{ lv.name }}</span>
          <span v-if="lv.tag" class="m600 sec-lvc__tag" :class="{ 'sec-lvc__tag--key': lv.tagKey }">{{ lv.tag }}</span>
        </div>

        <div class="sec-lvc__mid">
          <v-tooltip :text="tip(lv)" location="top">
            <template #activator="{ props: tipProps }">
              <div class="sec-lvc__bars" role="img" :aria-label="tip(lv)" v-bind="tipProps">
                <div class="sec-lvc__track">
                  <!-- Vue owns the final width (span); GSAP owns the fill inside it (bar). -->
                  <div class="sec-lvc__span" :style="{ width: pct(lv) + '%' }">
                    <div class="sec-lvc__bar">
                      <span class="sec-lvc__head" aria-hidden="true"></span>
                    </div>
                  </div>
                </div>
                <div class="sec-lvc__x" :class="{ 'sec-lvc__x--on': lv.practices }"></div>
              </div>
            </template>
          </v-tooltip>
          <p class="m500 sec-lvc__truth">{{ lv.truth }}</p>
        </div>

        <div class="o600 sec-lvc__val sec-num">
          <span class="sec-lvc__count">{{ lv.controls }}</span>
          <span class="m700 sec-lvc__val-label">{{
            lv.practices ? labels.plusPractices(lv.practices) : labels.ofControls(total)
          }}</span>
        </div>

        <div class="m500 sec-lvc__unlock">
          <b class="m700">{{ unlocksLabel }}</b>{{ lv.unlocks }}
        </div>
      </div>
    </div>

    <div class="m500 sec-lvc__legend" data-reveal>
      <span><i class="sec-lvc__swatch"></i>{{ legend.controls }}</span>
      <span><i class="sec-lvc__swatch sec-lvc__swatch--x"></i>{{ legend.practices }}</span>
    </div>
  </div>
</template>

<script setup>
// Maturity levels chart: one row per level, bar = controls of the baseline, dark tail =
// Level 4 practices beyond it.
//
// Motion (GSAP 3 core + ScrollTrigger): when the chart scrolls into view, rows fade up in
// turn, then each bar grows from 0 while its number counts up from 0. Bar and number are
// driven by ONE tween of a single progress value (0 -> 1), so they stay in sync on every
// frame: width = progress x target%, number = round(progress x controls). Level 4's dark
// tail then extends while "+ 0 practices" counts up to "+ 5 practices", also from one value.
// A light head on each bar marks the moving edge and fades out once it settles.
import { ref } from 'vue';
import { gsap } from '@/plugins/gsap';
import { useMotion } from '@/composables/security/useMotion';

const props = defineProps({
  levels: { type: Array, required: true }, // [{ n, name, tag, tagKey, controls, practices, truth, unlocks }]
  total: { type: Number, default: 32 },
  unlocksLabel: { type: String, default: 'Unlocks' },
  legend: { type: Object, required: true }, // { controls, practices }
  labels: { type: Object, required: true }, // { ofControls(total), plusPractices(n), tip(lv, total) }
});

const root = ref(null);

const pct = (lv) => ((lv.controls / props.total) * 100).toFixed(2);

const tip = (lv) => props.labels.tip(lv, props.total);

const GROW = 1.4; // seconds for a bar to reach its value
const ROW_GAP = 0.18; // stagger between rows, so the eye can follow top to bottom
const TAIL = 0.6; // Level 4 practices tail

useMotion(root, (el) => {
  const rows = gsap.utils.toArray('.sec-lvc__row', el);

  const tl = gsap.timeline({
    scrollTrigger: { trigger: el, start: 'top 75%', once: true },
    defaults: { ease: 'power3.out' },
  });

  // Rows rise in first (renders their hidden start state immediately, before the trigger).
  tl.from(rows, { autoAlpha: 0, y: 16, duration: 0.5, ease: 'power2.out', stagger: ROW_GAP * 0.6 });

  rows.forEach((row, i) => {
    const lv = props.levels[i];
    const bar = row.querySelector('.sec-lvc__bar'); // fills 0-100% of its span (the target width)
    const head = row.querySelector('.sec-lvc__head');
    const count = row.querySelector('.sec-lvc__count');
    const label = row.querySelector('.sec-lvc__val-label');
    const start = 0.3 + i * ROW_GAP;

    // Zero state until the chart is reached.
    bar.style.width = '0%';
    count.textContent = '0';

    // One value drives both the bar and the number.
    const state = { p: 0 };
    tl.to(
      state,
      {
        p: 1,
        duration: GROW,
        onUpdate: () => {
          bar.style.width = `${100 * state.p}%`;
          count.textContent = String(Math.round(lv.controls * state.p));
        },
      },
      start
    );
    if (lv.controls > 0) {
      tl.fromTo(head, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2, ease: 'none' }, start);
      tl.to(head, { autoAlpha: 0, duration: 0.5, ease: 'power1.out' }, start + GROW - 0.3);
    }

    // Level 4: the tail extends after the bar is full, its practices counting up with it.
    const tail = row.querySelector('.sec-lvc__x--on');
    if (tail && lv.practices) {
      const extra = { p: 0 };
      label.textContent = props.labels.plusPractices(0);
      gsap.set(tail, { scaleX: 0, transformOrigin: 'left center' });
      tl.to(
        extra,
        {
          p: 1,
          duration: TAIL,
          ease: 'power2.out',
          onUpdate: () => {
            gsap.set(tail, { scaleX: extra.p });
            label.textContent = props.labels.plusPractices(Math.round(lv.practices * extra.p));
          },
        },
        start + GROW - 0.15
      );
    }
  });

  // On unmount or reduced motion, put every row back to its final, static values.
  return () => {
    rows.forEach((row, i) => {
      const lv = props.levels[i];
      row.querySelector('.sec-lvc__bar').style.width = '';
      row.querySelector('.sec-lvc__count').textContent = String(lv.controls);
      row.querySelector('.sec-lvc__val-label').textContent = lv.practices
        ? props.labels.plusPractices(lv.practices)
        : props.labels.ofControls(props.total);
      const tail = row.querySelector('.sec-lvc__x--on');
      if (tail) gsap.set(tail, { clearProps: 'transform' });
    });
  };
});
</script>

<style scoped>
.sec-lvc-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--sec-s3);
}

.sec-lvc {
  background: var(--sec-surface);
  border: 1px solid var(--sec-line);
  border-radius: var(--sec-radius-card);
  padding: 4px var(--sec-s3);
}

.sec-lvc__row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px var(--sec-s2);
  align-items: start;
  padding-block: 20px;
  border-bottom: 1px solid var(--sec-line);
}

.sec-lvc__row:last-child {
  border-bottom: 0;
}

.sec-lvc__label {
  grid-column: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sec-lvc__n {
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sec-ink-3);
}

.sec-lvc__name {
  font-size: 1.25rem;
  line-height: 1.2;
  color: var(--sec-ink);
}

.sec-lvc__tag {
  align-self: flex-start;
  margin-top: 6px;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--sec-ink);
  border: 1px solid var(--sec-line-strong);
  border-radius: var(--sec-radius-sm);
  padding: 3px 9px;
}

.sec-lvc__tag--key {
  border-color: var(--sec-accent);
  background: var(--sec-accent-subtle);
}

.sec-lvc__mid {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
  min-width: 0;
}

.sec-lvc__bars {
  display: flex;
  gap: 2px;
  align-items: center;
  cursor: default;
}

.sec-lvc__track {
  flex: 1;
  height: 22px;
  background: var(--sec-track);
  border-radius: 0 4px 4px 0;
  overflow: hidden;
}

.sec-lvc__span {
  height: 100%;
}

.sec-lvc__bar {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--sec-bar);
  border-radius: 0 4px 4px 0;
  will-change: width;
}

/* Moving edge of the bar while it grows; GSAP fades it in and out. */
.sec-lvc__head {
  position: absolute;
  top: 3px;
  bottom: 3px;
  right: 3px;
  width: 4px;
  border-radius: 2px;
  background: var(--sec-on-accent);
  box-shadow: 0 0 10px 2px var(--sec-accent-ring);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.sec-lvc__x {
  width: 48px;
  height: 22px;
  flex-shrink: 0;
}

.sec-lvc__x--on {
  background: var(--sec-bar-x);
  border-radius: 0 4px 4px 0;
}

.sec-lvc__truth {
  font-size: 0.9rem;
  line-height: 155%;
  color: var(--sec-ink-2);
}

.sec-lvc__val {
  grid-column: 2;
  grid-row: 1;
  text-align: right;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--sec-ink);
}

.sec-lvc__val-label {
  display: block;
  margin-top: 6px;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sec-ink-3);
}

.sec-lvc__unlock {
  grid-column: 1 / -1;
  font-size: 0.95rem;
  line-height: 150%;
  color: var(--sec-ink);
}

.sec-lvc__unlock b {
  display: block;
  margin-bottom: 4px;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sec-ink-3);
}

.sec-lvc__legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sec-s3);
  font-size: 0.85rem;
  color: var(--sec-ink-2);
}

.sec-lvc__legend span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sec-lvc__swatch {
  display: block;
  width: 18px;
  height: 10px;
  border-radius: 0 3px 3px 0;
  background: var(--sec-bar);
}

.sec-lvc__swatch--x {
  background: var(--sec-bar-x);
}

@media only screen and (min-width: 960px) {
  .sec-lvc {
    padding-inline: var(--sec-s4);
  }
  .sec-lvc__row {
    grid-template-columns: 220px minmax(0, 1fr) 110px 240px;
    gap: var(--sec-s3);
  }
  .sec-lvc__label,
  .sec-lvc__mid,
  .sec-lvc__val,
  .sec-lvc__unlock {
    grid-column: auto;
    grid-row: auto;
  }
  .sec-lvc__val {
    text-align: left;
  }
}
</style>
